import { getActiveBaseUrl } from '@/lib/axios';
import { logger } from '@/lib/logger';

/**
 * Envelope returned by the backend's /api/price/asetqu-idr.
 */
interface AsetquPriceResponse {
  data: {
    priceIdr: number;
    timestamp: number;
    high24h: number;
    low24h: number;
    buyPrice: number;
    sellPrice: number;
  } | null;
}

/**
 * Indodax Price Data
 */
export interface IndodaxPriceData {
  priceIdr: number;
  timestamp: number;
  high24h: number;
  low24h: number;
  buyPrice: number;
  sellPrice: number;
}

/**
 * Service for fetching ASETQU prices from API
 */
class IndodaxService {
  // Served by our own backend rather than the asetqu-price-indodax service.
  // That service allow-lists origins one by one, so calling it straight from
  // the browser broke as soon as the app moved to a new host — and the catch
  // below turns that into a hard-coded price rather than a visible error.
  private getApiUrl(): string {
    return `${getActiveBaseUrl()}/api/price/asetqu-idr`;
  }

  /**
   * Fetch current ASETQU/IDR price from API
   */
  async getAsetquIdrPrice(): Promise<IndodaxPriceData> {
    const apiUrl = this.getApiUrl();
    try {
      logger.log('[Indodax] Fetching price from:', apiUrl);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AsetquPriceResponse = await response.json();

      if (!data.data) {
        throw new Error('Invalid response from API');
      }

      logger.log('[Indodax] Price fetched successfully:', data.data.priceIdr, 'IDR');

      return data.data;
    } catch (error) {
      logger.error('Failed to fetch ASETQU/IDR price:', error);
      // Fallback to reasonable default price
      return {
        priceIdr: 450000,
        timestamp: Date.now(),
        high24h: 463295,
        low24h: 450000,
        buyPrice: 450000,
        sellPrice: 451000,
      };
    }
  }

  /**
   * Calculate IDR amount from ASETQU amount
   */
  calculateIdrAmount(asetquAmount: number, priceIdr: number): number {
    return asetquAmount * priceIdr;
  }

  /**
   * Format IDR amount
   */
  formatIdr(amount: number): string {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
}

export const indodaxService = new IndodaxService();
